import { useCallback, useEffect, useState } from 'react';
import { portalService } from '../../../services/portal';
import type { OrderDocumentItem } from '../../../types/portal';

export interface UseOrderDocumentsResult {
  documents: OrderDocumentItem[];
  loading: boolean;
  fetched: boolean;
  refetch: () => void;
}

/**
 * Lazily loads documents for an order when its tab is active and the modal
 * is open. `fetched` is the loop terminator: it flips exactly once per
 * (modal-open, order) so the effect cannot refetch on its own state changes —
 * important when the API resolves with an empty list.
 *
 * Reset is computed during render (React's recommended pattern for derived
 * state on prop change — see
 * https://react.dev/learn/you-might-not-need-an-effect#resetting-all-state-when-a-prop-changes).
 *
 * `loading` is derived from (`shouldFetch && !fetched`) rather than tracked as
 * its own state, which avoids a synchronous setState in the fetch effect.
 *
 * `refetch` lets callers (e.g. the upload + delete flows on the Documents
 * panel) repopulate the list after mutating it without remounting the modal.
 */
export function useOrderDocuments(
  orderId: string | null | undefined,
  isOpen: boolean,
  isActive: boolean,
): UseOrderDocumentsResult {
  const [documents, setDocuments] = useState<OrderDocumentItem[]>([]);
  const [fetched, setFetched] = useState(false);

  const shouldFetch = isActive && !!orderId && isOpen;

  // Reset gate when the (orderId, isOpen) pair changes. React discards the
  // first-render output and re-renders with the reset state.
  const sessionKey = `${orderId ?? ''}::${isOpen ? '1' : '0'}`;
  const [prevSessionKey, setPrevSessionKey] = useState(sessionKey);
  if (prevSessionKey !== sessionKey) {
    setPrevSessionKey(sessionKey);
    setDocuments([]);
    setFetched(false);
  }

  useEffect(() => {
    if (!shouldFetch || fetched) return;
    let cancelled = false;
    portalService
      .getOrderDocuments(orderId as string)
      .then((res) => {
        if (!cancelled) setDocuments(res.documents);
      })
      .catch(() => {
        if (!cancelled) setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setFetched(true);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, shouldFetch, fetched]);

  const refetch = useCallback(() => {
    // Flip the terminator so the effect runs again on the next render.
    setFetched(false);
  }, []);

  return {
    documents,
    loading: shouldFetch && !fetched,
    fetched,
    refetch,
  };
}
